import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import Logo from "./logo";
import { useTranslation } from "react-i18next";
import { Menu, X } from "lucide-react";
import WhatsappCta from "./whatsapp-cta";
import { Languages } from "./language-switcher";
import SeasonSwitcher from "./season-switcher";

/*
  ПЛАВАЮЩАЯ ШАПКА.
  Обычная шапка живёт на первом экране. Когда страницу пролистали вниз,
  она уезжает — и чтобы не возвращаться наверх ради меню, сверху появляется
  узкая полоска с логотипом, ссылками, кнопкой заявки и «тремя полосками»
  (бургер) на телефоне.

  Ведёт себя по-разному:
    ноутбук/компьютер — висит сверху постоянно, места на экране хватает;
    телефон — выезжает только при движении пальцем ВВЕРХ и прячется при
    движении вниз, чтобы не съедать и без того маленький экран.

  Что можно крутить:
    SHOW_AFTER — на сколько пикселей надо уйти вниз, чтобы полоска включилась
                 (выше этой отметки видна обычная шапка, дубль не нужен);
    SCROLL_TOLERANCE — насколько резко надо дёрнуть на телефоне, чтобы
                 полоска среагировала;
    DESKTOP_QUERY — с какой ширины экрана шапка считается «всегда сверху».
*/
const SHOW_AFTER = 320;
const SCROLL_TOLERANCE = 6;
const DESKTOP_QUERY = "(min-width: 1024px)";

export default function StickyNav({ links = [] }) {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const desktop = window.matchMedia(DESKTOP_QUERY);
    let lastY = window.scrollY;
    let ticking = false;

    const update = () => {
      ticking = false;
      const y = window.scrollY;
      const up = y < lastY - SCROLL_TOLERANCE;
      const down = y > lastY + SCROLL_TOLERANCE;

      if (y < SHOW_AFTER) {
        // мы у самого верха — работает настоящая шапка страницы
        setVisible(false);
        setOpen(false);
      } else if (desktop.matches) {
        // на большом экране полоска просто всегда сверху
        setVisible(true);
      } else if (up) {
        setVisible(true);
      } else if (down) {
        setVisible(false);
        setOpen(false);
      }

      if (up || down) lastY = y;
    };

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    };

    update(); // на случай, если страницу открыли уже прокрученной
    window.addEventListener("scroll", onScroll, { passive: true });
    // при смене размера окна/повороте телефона правило меняется
    desktop.addEventListener("change", update);
    return () => {
      window.removeEventListener("scroll", onScroll);
      desktop.removeEventListener("change", update);
    };
  }, []);

  if (typeof document === "undefined") return null;

  return createPortal(
    <div
      className={`fixed inset-x-0 top-0 z-[60] transition-transform duration-300 ease-out ${
        visible ? "translate-y-0" : "-translate-y-full"
      }`}
      aria-hidden={!visible}
    >
      <div className="bg-kj-dark/92 backdrop-blur-md border-b border-kj-line">
        <div className="max-w-[1526px] mx-auto px-4 h-14 flex items-center justify-between gap-4">
          <Logo size="compact" />

          <nav className="hidden lg:flex items-center gap-x-2">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                className="px-3 py-1.5 text-sm font-bold uppercase tracking-[0.12em] text-kj-ink hover:text-kj-gold transition-colors"
              >
                {l.label}
              </a>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-x-3">
            {/* compact: в узкой полосе для подписи «Сакура» места нет,
                остаётся только знак сезона */}
            <SeasonSwitcher compact />
            <Languages variant="compact" />
            <WhatsappCta
              variant="outline"
              size="sm"
              className="border-kj-gold text-kj-gold bg-transparent hover:bg-kj-gold hover:text-kj-dark uppercase tracking-[0.1em] text-xs font-bold rounded-sm"
            />
          </div>

          {/* Те самые «три полоски» на телефоне и планшете */}
          <button
            type="button"
            className="lg:hidden -mr-2 p-2.5 min-h-[44px] min-w-[44px] flex items-center justify-center rounded-md text-kj-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-kj-gold"
            onClick={() => setOpen((v) => !v)}
            aria-label={t("header.menu")}
            aria-expanded={open}
            tabIndex={visible ? 0 : -1}
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden mx-4 mt-2 bg-kj-card border border-kj-line rounded-xl p-4 flex flex-col gap-3 shadow-2xl">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={() => setOpen(false)}
              className="rounded-md border border-kj-line px-4 py-3 text-sm font-bold uppercase tracking-[0.12em] text-kj-ink hover:border-kj-gold hover:text-kj-gold transition-colors"
            >
              {l.label}
            </a>
          ))}
          <WhatsappCta
            variant="outline"
            size="default"
            className="w-full border-kj-gold text-kj-gold bg-transparent hover:bg-kj-gold hover:text-kj-dark uppercase tracking-[0.1em] text-xs font-bold rounded-sm"
          />
          <div className="border-t border-kj-line pt-2 flex items-center justify-between gap-3">
            <Languages variant="compact" />
            <SeasonSwitcher />
          </div>
        </div>
      )}
    </div>,
    document.body
  );
}
